/**
 * AI Sales Copilot — Settings Page (Session 3 + 7 + 8).
 *
 * Tabbed configuration UI for everything the AI Copilot can do. All
 * fields map 1-to-1 with /app/backend/ai_copilot/settings.py whitelist.
 *
 * Tabs:
 *   1. General           — master enable + per-feature toggles
 *   2. Tone & Brand      — voice, language, brand description
 *   3. Policies          — RAG context: delivery / return / payment / discount
 *   4. Auto-Reply        — Session 7 confidence gating + quiet hours
 *   5. Handoff           — Session 7 human handoff settings
 *   6. Tools             — Session 8 translation & rewrite toggles
 *   7. Usage             — daily token / call rollup
 *   8. Analytics         — feature KPIs (task reasons, intents, lead tiers)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Save, Loader2, AlertTriangle, RefreshCw, MessageSquare,
  BookOpen, Shield, Languages, Activity, BarChart3, Hand, Settings,
  Clock, Plus, Trash2, Database, FileText, Link2, ShoppingBag, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput, TimeInput } from '@/components/ui/date-input';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TONES = [
  { v: 'friendly',     l: 'Friendly' },
  { v: 'premium',      l: 'Premium' },
  { v: 'professional', l: 'Professional' },
  { v: 'luxury',       l: 'Luxury' },
  { v: 'short_direct', l: 'Short & Direct' },
  { v: 'founder',      l: 'Founder Voice' },
  { v: 'supportive',   l: 'Supportive' },
];

const MODES = [
  { v: 'suggest_only',         l: 'Suggest only (human approves every reply)' },
  { v: 'faq_only',             l: 'Auto-reply to FAQs only' },
  { v: 'sales_high_confidence', l: 'Auto-reply when confidence is high' },
  { v: 'full_auto',            l: 'Full auto — all replies' },
];

const LANGS = [
  ['en', 'English'], ['hi', 'Hindi'], ['ar', 'Arabic'], ['es', 'Spanish'],
  ['fr', 'French'], ['de', 'German'], ['pt', 'Portuguese'], ['it', 'Italian'],
  ['ja', 'Japanese'], ['zh', 'Chinese'], ['ru', 'Russian'], ['tr', 'Turkish'],
];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AICopilotSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});
  const [error, setError] = useState('');
  const [tab, setTab] = useState('general');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/ai-copilot/settings`, { headers: authHeaders() });
      setSettings(res.data);
      setDirty({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
    setDirty(d => ({ ...d, [key]: value }));
  };

  const handleSave = async () => {
    if (!Object.keys(dirty).length) {
      toast.info('Nothing changed');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/ai-copilot/settings`, dirty, { headers: authHeaders() });
      setSettings(res.data);
      setDirty({});
      toast.success('Copilot settings saved');
      // Bust local cache used by inbox components
      try { localStorage.setItem('ai_copilot_enabled', String(!!res.data.enabled)); } catch {}
    } catch (e) {
      // Surface as much detail as possible. The old toast just said
      // "Failed to save" — useless for prod debugging. Now we show
      // HTTP status + backend `detail` (string OR list of pydantic
      // errors) OR fall back to the JS error message.
      let detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
      } else if (detail && typeof detail !== 'string') {
        detail = JSON.stringify(detail);
      }
      const status = e?.response?.status;
      const msg = [
        status ? `[${status}]` : null,
        detail || e?.message || 'Failed to save',
      ].filter(Boolean).join(' ');
      toast.error(msg, { duration: 8000 });
      // Also log to console with the full dirty payload — invaluable
      // when an operator screenshots dev tools.
      console.error('[AI Copilot Settings] save failed', {
        status, detail, payload: dirty, error: e,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasDirty = Object.keys(dirty).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-wt-text-muted">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading Copilot settings…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded p-4 text-rose-800" data-testid="ai-settings-error">
          <AlertTriangle className="h-5 w-5 flex-none mt-0.5" />
          <div>
            <div className="font-medium">Couldn't load settings</div>
            <div className="text-sm">{error}</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={load} data-testid="ai-settings-retry">
              <RefreshCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (!settings) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="ai-copilot-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-wt-text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-wt-gold" />
            AI Sales Copilot
          </h1>
          <p className="text-sm text-wt-text-muted">
            Configure how the Copilot suggests, replies, hands off, translates, and reports usage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {settings.enabled
            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="ai-master-status">Copilot active</Badge>
            : <Badge variant="outline" className="text-wt-text-muted" data-testid="ai-master-status">Copilot off</Badge>}
          <Button onClick={handleSave} disabled={!hasDirty || saving}
                  className="bg-wt-gold hover:bg-wt-gold/90 text-white"
                  data-testid="ai-settings-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 lg:grid-cols-10 gap-1 mb-4" data-testid="ai-settings-tabs">
          <TabsTrigger value="general"   data-testid="tab-general"><Settings className="h-3 w-3 mr-1" />General</TabsTrigger>
          <TabsTrigger value="tone"      data-testid="tab-tone"><MessageSquare className="h-3 w-3 mr-1" />Tone</TabsTrigger>
          <TabsTrigger value="policies"  data-testid="tab-policies"><BookOpen className="h-3 w-3 mr-1" />Policies</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge"><Database className="h-3 w-3 mr-1" />Knowledge</TabsTrigger>
          <TabsTrigger value="autoreply" data-testid="tab-autoreply"><Shield className="h-3 w-3 mr-1" />Auto-Reply</TabsTrigger>
          <TabsTrigger value="hours"     data-testid="tab-hours"><Clock className="h-3 w-3 mr-1" />Hours</TabsTrigger>
          <TabsTrigger value="handoff"   data-testid="tab-handoff"><Hand className="h-3 w-3 mr-1" />Handoff</TabsTrigger>
          <TabsTrigger value="tools"     data-testid="tab-tools"><Languages className="h-3 w-3 mr-1" />Tools</TabsTrigger>
          <TabsTrigger value="usage"     data-testid="tab-usage"><Activity className="h-3 w-3 mr-1" />Usage</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-3 w-3 mr-1" />Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <SectionGeneral settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="tone">
          <SectionTone settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="policies">
          <SectionPolicies settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="knowledge">
          <SectionKnowledge />
        </TabsContent>
        <TabsContent value="autoreply">
          <SectionAutoReply settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="hours">
          <SectionOfficeHours settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="handoff">
          <SectionHandoff settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="tools">
          <SectionTools settings={settings} update={update} />
        </TabsContent>
        <TabsContent value="usage">
          <SectionUsage />
        </TabsContent>
        <TabsContent value="analytics">
          <SectionAnalytics />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

// ───── Reusable Row ────────────────────────────────────────────────────
function Row({ title, hint, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-wt-border last:border-0"
         data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-wt-text-primary">{title}</div>
        {hint && <div className="text-xs text-wt-text-muted mt-0.5">{hint}</div>}
      </div>
      <div className="flex-none">{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-wt-paper/30 border border-wt-border rounded-lg p-4 mb-4">
      <h2 className="text-base font-semibold text-wt-text-primary mb-2">{title}</h2>
      {children}
    </div>
  );
}

// ───── General ─────────────────────────────────────────────────────────
function SectionGeneral({ settings, update }) {
  const toggle = (k, label, hint) => (
    <Row title={label} hint={hint} testid={`row-${k}`}>
      <Switch checked={!!settings[k]} onCheckedChange={v => update(k, v)} data-testid={`switch-${k}`} />
    </Row>
  );
  return (
    <Section title="Feature switches">
      {toggle('enabled', 'Enable AI Copilot', 'Master switch — turn this off to disable everything.')}
      {toggle('reply_suggestions_enabled', 'Reply suggestions',  'Suggest a reply for every incoming inbox message.')}
      {toggle('lead_scoring_enabled',      'Lead scoring',       'Score every conversation cold / warm / hot / burning.')}
      {toggle('chat_summary_enabled',      'Chat summaries',     'Generate a one-glance summary of long threads.')}
      {toggle('product_recommendations_enabled', 'Product recommendations', 'Suggest matching products from your catalogue.')}
      {toggle('follow_up_enabled',         'Follow-up tasks',    'Auto-create tasks for hot leads who stop replying.')}
      {toggle('delivery_intelligence_enabled', 'Delivery intelligence', 'Suggest carrier + ETA based on history.')}
      {toggle('burning_leads_enabled',     'Burning leads',      'Surface ultra-hot leads in a dedicated tray.')}
      {toggle('abandoned_checkout_hook_enabled', 'Abandoned checkout hook', 'Inject Shopify abandoned-cart context into chat.')}
    </Section>
  );
}

// ───── Tone & Brand ────────────────────────────────────────────────────
function SectionTone({ settings, update }) {
  return (
    <Section title="Tone & brand">
      <Row title="Brand name" hint="Shown in greetings and signatures." testid="row-brand_name">
        <Input value={settings.brand_name || ''} onChange={e => update('brand_name', e.target.value)}
               className="w-56" data-testid="input-brand_name" />
      </Row>
      <Row title="Brand tone" testid="row-brand_tone">
        <Select value={settings.brand_tone || 'friendly'} onValueChange={v => update('brand_tone', v)}>
          <SelectTrigger className="w-56" data-testid="select-brand_tone"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TONES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>
      <Row title="Brand description" hint="Used as context in every reply. 2-3 sentences max."
           testid="row-brand_description">
        <Textarea value={settings.brand_description || ''} onChange={e => update('brand_description', e.target.value)}
                  className="w-96 min-h-[80px]" rows={3} data-testid="textarea-brand_description" />
      </Row>
      <Row title="Default reply language" testid="row-default_reply_language">
        <Select value={settings.default_reply_language || 'en'} onValueChange={v => update('default_reply_language', v)}>
          <SelectTrigger className="w-56" data-testid="select-default_reply_language"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>
      <Row title="Avoid words" hint="Comma-separated. The Copilot will never use these in replies."
           testid="row-avoid_words">
        <Input value={(settings.avoid_words || []).join(', ')}
               onChange={e => update('avoid_words', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
               className="w-96" data-testid="input-avoid_words" />
      </Row>
    </Section>
  );
}

// ───── Policies ────────────────────────────────────────────────────────
function SectionPolicies({ settings, update }) {
  const polField = (k, label, placeholder) => (
    <Row title={label} testid={`row-${k}`}>
      <Textarea value={settings[k] || ''} onChange={e => update(k, e.target.value)}
                placeholder={placeholder}
                className="w-96 min-h-[80px]" rows={3} data-testid={`textarea-${k}`} />
    </Row>
  );
  return (
    <Section title="Policies & guidance">
      {polField('delivery_policy',    'Delivery policy',     'e.g. Free over AED 200. UAE 1-2 days; rest 3-5 days.')}
      {polField('installation_policy','Installation policy', 'e.g. Pay-on-install AED 150 for sofas.')}
      {polField('return_policy',      'Return policy',       'e.g. 14-day window, fees apply for fitted items.')}
      {polField('payment_policy',     'Payment policy',      'Accepted methods, COD areas, BNPL terms.')}
      {polField('discount_policy',    'Discount policy',     'Floor discount, who can approve overrides.')}
      {polField('escalation_rules',   'Escalation rules',    'When to mark a conversation for a human.')}
    </Section>
  );
}

// ───── Auto-Reply ──────────────────────────────────────────────────────
function SectionAutoReply({ settings, update }) {
  return (
    <Section title="Auto-reply">
      <Row title="Auto-send enabled" hint="When off, every suggestion is human-approved before sending."
           testid="row-auto_send_enabled">
        <Switch checked={!!settings.auto_send_enabled} onCheckedChange={v => update('auto_send_enabled', v)}
                data-testid="switch-auto_send_enabled" />
      </Row>
      <Row title="Auto-reply mode" testid="row-auto_reply_mode">
        <Select value={settings.auto_reply_mode || 'suggest_only'} onValueChange={v => update('auto_reply_mode', v)}>
          <SelectTrigger className="w-72" data-testid="select-auto_reply_mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MODES.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>
      <Row title="Minimum confidence for auto-send (%)" hint="0-100. Replies below this stay as suggestions."
           testid="row-min_confidence_for_auto_send">
        <Input type="number" min={0} max={100} className="w-32"
               value={settings.min_confidence_for_auto_send ?? 85}
               onChange={e => update('min_confidence_for_auto_send', Number(e.target.value))}
               data-testid="input-min_confidence" />
      </Row>
      <Row title="Max auto-replies per conversation" hint="Hard cap to avoid runaway threads."
           testid="row-max_auto_replies_per_conversation">
        <Input type="number" min={0} max={50} className="w-32"
               value={settings.max_auto_replies_per_conversation ?? 3}
               onChange={e => update('max_auto_replies_per_conversation', Number(e.target.value))}
               data-testid="input-max_auto_replies" />
      </Row>
      <Row title="High-value order threshold" hint="Auto-reply paused on conversations whose customer's last order exceeds this."
           testid="row-high_value_order_threshold">
        <Input type="number" min={0} className="w-32"
               value={settings.high_value_order_threshold ?? 0}
               onChange={e => update('high_value_order_threshold', Number(e.target.value))}
               data-testid="input-high_value" />
      </Row>
      <Row title="Stop automation when assigned" testid="row-stop_when_assigned">
        <Switch checked={!!settings.stop_automation_when_assigned}
                onCheckedChange={v => update('stop_automation_when_assigned', v)}
                data-testid="switch-stop_when_assigned" />
      </Row>
      <Row title="Stop automation when human requested" testid="row-stop_when_human">
        <Switch checked={!!settings.stop_automation_when_human_requested}
                onCheckedChange={v => update('stop_automation_when_human_requested', v)}
                data-testid="switch-stop_when_human" />
      </Row>
      <Row title="Auto-reply delay (seconds)" hint="A small delay feels natural; 0 sends immediately."
           testid="row-auto_reply_delay_seconds">
        <Input type="number" min={0} max={600} className="w-32"
               value={settings.auto_reply_delay_seconds ?? 8}
               onChange={e => update('auto_reply_delay_seconds', Number(e.target.value))}
               data-testid="input-auto_reply_delay" />
      </Row>
      <Row title="Quiet hours" hint="Auto-reply paused inside this window. 24-hour HH:MM."
           testid="row-quiet_hours">
        <div className="flex items-center gap-2">
          <TimeInput  className="w-28"
                 value={settings.auto_send_quiet_hours_start || ''}
                 onChange={e => update('auto_send_quiet_hours_start', e.target.value || null)}
                 data-testid="input-quiet_start" />
          <span className="text-xs text-wt-text-muted">to</span>
          <TimeInput  className="w-28"
                 value={settings.auto_send_quiet_hours_end || ''}
                 onChange={e => update('auto_send_quiet_hours_end', e.target.value || null)}
                 data-testid="input-quiet_end" />
        </div>
      </Row>

      {/* Always-reply fallback — sends a courtesy message when the AI
          would otherwise skip (low confidence, needs_human, mode-block,
          no candidate). One per conversation by default. */}
      <div className="mt-4 pt-3 border-t border-wt-border">
        <div className="text-sm font-semibold text-wt-text-primary mb-1">
          Always-reply fallback
        </div>
        <div className="text-xs text-wt-text-muted mb-2">
          When the AI can't confidently reply, send a polite "we'll get back to you" message
          so the customer is never left waiting.
        </div>
      </div>
      <Row title="Send fallback when AI skips"
           hint="Triggers on: low confidence, needs-human intent, mode-blocked greetings, no candidate reply."
           testid="row-low_confidence_fallback_enabled">
        <Switch checked={!!settings.low_confidence_fallback_enabled}
                onCheckedChange={v => update('low_confidence_fallback_enabled', v)}
                data-testid="switch-low_confidence_fallback_enabled" />
      </Row>
      <Row title="Fallback message"
           hint="Sent verbatim when AI can't reply. Adapts to office hours automatically when enabled below."
           testid="row-low_confidence_fallback_message">
        <Textarea value={settings.low_confidence_fallback_message || ''}
                  onChange={e => update('low_confidence_fallback_message', e.target.value)}
                  className="w-96 min-h-[80px]" rows={3}
                  data-testid="textarea-low_confidence_fallback_message" />
      </Row>
      <Row title="Max fallbacks per conversation"
           hint="Hard cap to avoid sending the same template repeatedly. 0 = no limit."
           testid="row-low_confidence_fallback_max">
        <Input type="number" min={0} max={20} className="w-32"
               value={settings.low_confidence_fallback_max_per_conversation ?? 1}
               onChange={e => update('low_confidence_fallback_max_per_conversation', Number(e.target.value))}
               data-testid="input-low_confidence_fallback_max" />
      </Row>
      <Row title="Adapt to office hours"
           hint='When closed/holiday, prepends a smart line like "We&apos;re closed for Diwali — back tomorrow at 09:00."'
           testid="row-low_confidence_fallback_adapt">
        <Switch checked={settings.low_confidence_fallback_adapt_to_hours !== false}
                onCheckedChange={v => update('low_confidence_fallback_adapt_to_hours', v)}
                data-testid="switch-low_confidence_fallback_adapt" />
      </Row>
    </Section>
  );
}

// ───── Office Hours & Holidays ─────────────────────────────────────────
const _COMMON_TZ = [
  'UTC',
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Singapore',
  'Asia/Bangkok', 'Asia/Hong_Kong', 'Asia/Tokyo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Sao_Paulo',
  'Australia/Sydney',
];
const _DAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'],
  ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

function SectionOfficeHours({ settings, update }) {
  const hours = settings.office_hours || {};
  const holidays = settings.holidays || [];

  const setDay = (day, patch) => {
    const next = { ...hours, [day]: { ...(hours[day] || {}), ...patch } };
    update('office_hours', next);
  };
  const addHoliday = () => {
    const today = new Date().toISOString().slice(0, 10);
    const exists = holidays.some(h => h.date === today);
    const seed = exists ? '' : today;
    update('holidays', [...holidays, { date: seed, label: '' }]);
  };
  const updateHoliday = (idx, patch) => {
    update('holidays', holidays.map((h, i) => i === idx ? { ...h, ...patch } : h));
  };
  const removeHoliday = (idx) => {
    update('holidays', holidays.filter((_, i) => i !== idx));
  };

  return (
    <Section title="Office hours & holidays">
      <Row title="Office hours awareness"
           hint="Inject today's open/close + holidays into AI replies so customers get accurate ETAs."
           testid="row-office_hours_enabled">
        <Switch checked={!!settings.office_hours_enabled}
                onCheckedChange={v => update('office_hours_enabled', v)}
                data-testid="switch-office_hours_enabled" />
      </Row>
      <Row title="Pause auto-reply outside office hours"
           hint="When ON, the Copilot won't auto-send replies when closed (suggestions still work)."
           testid="row-pause_auto_reply_outside_office_hours">
        <Switch checked={!!settings.pause_auto_reply_outside_office_hours}
                onCheckedChange={v => update('pause_auto_reply_outside_office_hours', v)}
                data-testid="switch-pause_outside_hours" />
      </Row>
      <Row title="Timezone" hint="IANA timezone — used to compute local time, holiday boundaries, and reopen day."
           testid="row-timezone">
        <Select value={settings.timezone || 'UTC'} onValueChange={v => update('timezone', v)}>
          <SelectTrigger className="w-56" data-testid="select-timezone"><SelectValue /></SelectTrigger>
          <SelectContent>
            {_COMMON_TZ.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>

      {/* Per-weekday schedule */}
      <div className="mt-4">
        <div className="text-sm font-medium text-wt-text-primary mb-2">Weekly schedule</div>
        <div className="space-y-1.5">
          {_DAYS.map(([k, label]) => {
            const cfg = hours[k] || { open: '09:00', close: '18:00', closed: false };
            const closed = !!cfg.closed;
            return (
              <div key={k} className="flex items-center gap-3 text-sm" data-testid={`row-day-${k}`}>
                <div className="w-24 text-wt-text-primary">{label}</div>
                <Switch checked={!closed} onCheckedChange={v => setDay(k, { closed: !v })}
                        data-testid={`switch-day-${k}`} />
                <span className="text-xs text-wt-text-muted w-14">{closed ? 'Closed' : 'Open'}</span>
                <TimeInput  className="w-28" disabled={closed}
                       value={cfg.open || '09:00'}
                       onChange={e => setDay(k, { open: e.target.value })}
                       data-testid={`input-day-open-${k}`} />
                <span className="text-xs text-wt-text-muted">to</span>
                <TimeInput  className="w-28" disabled={closed}
                       value={cfg.close || '18:00'}
                       onChange={e => setDay(k, { close: e.target.value })}
                       data-testid={`input-day-close-${k}`} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Holidays */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-wt-text-primary">Holidays</div>
            <div className="text-xs text-wt-text-muted">
              The AI will mention these dates when explaining dispatch delays.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={addHoliday} data-testid="btn-add-holiday">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add holiday
          </Button>
        </div>
        {holidays.length === 0 ? (
          <div className="text-xs text-wt-text-muted py-2">No holidays added.</div>
        ) : (
          <div className="space-y-1.5">
            {holidays.map((h, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`row-holiday-${idx}`}>
                <DateInput  className="w-44"
                       value={h.date || ''}
                       onChange={e => updateHoliday(idx, { date: e.target.value })}
                       data-testid={`input-holiday-date-${idx}`} />
                <Input className="flex-1" placeholder="e.g. Diwali"
                       value={h.label || ''}
                       onChange={e => updateHoliday(idx, { label: e.target.value })}
                       data-testid={`input-holiday-label-${idx}`} />
                <Button size="sm" variant="ghost" onClick={() => removeHoliday(idx)}
                        className="text-rose-600 hover:text-rose-700"
                        data-testid={`btn-remove-holiday-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

// ───── Handoff ─────────────────────────────────────────────────────────
function SectionHandoff({ settings, update }) {
  return (
    <Section title="Human handoff">
      <Row title="Enable handoff" hint="Operators can flag a conversation for human help with one click."
           testid="row-human_handoff_enabled">
        <Switch checked={!!settings.human_handoff_enabled}
                onCheckedChange={v => update('human_handoff_enabled', v)}
                data-testid="switch-human_handoff_enabled" />
      </Row>
      <Row title="Auto-handoff below confidence (%)"
           hint="If the Copilot's reply confidence falls under this, the thread is auto-flagged for human handoff."
           testid="row-handoff_below_confidence">
        <Input type="number" min={0} max={100} className="w-32"
               value={settings.handoff_below_confidence ?? 60}
               onChange={e => update('handoff_below_confidence', Number(e.target.value))}
               data-testid="input-handoff_below" />
      </Row>
      <Row title="Extra notify user IDs" hint="Comma-separated user IDs to notify on every handoff."
           testid="row-handoff_notify_user_ids">
        <Input className="w-96"
               value={(settings.handoff_notify_user_ids || []).join(', ')}
               onChange={e => update('handoff_notify_user_ids',
                 e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
               data-testid="input-notify_users" />
      </Row>
    </Section>
  );
}

// ───── Tools (Translate + Rewrite + Usage) ─────────────────────────────
function SectionTools({ settings, update }) {
  return (
    <Section title="Translation & rewrite">
      <Row title="Translation tool" hint="Operators can translate incoming/outgoing messages on the fly."
           testid="row-translation_enabled">
        <Switch checked={!!settings.translation_enabled}
                onCheckedChange={v => update('translation_enabled', v)}
                data-testid="switch-translation_enabled" />
      </Row>
      <Row title="Rewrite tools" hint="Shorter, friendlier, formal, fix-grammar, longer, persuasive."
           testid="row-rewrite_enabled">
        <Switch checked={!!settings.rewrite_enabled}
                onCheckedChange={v => update('rewrite_enabled', v)}
                data-testid="switch-rewrite_enabled" />
      </Row>
      <Row title="Primary language (translate target default)" testid="row-primary_language">
        <Select value={settings.primary_language || 'en'} onValueChange={v => update('primary_language', v)}>
          <SelectTrigger className="w-56" data-testid="select-primary_language"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>
      <Row title="Usage logging" hint="Track LLM token spend per day/feature. Required for the Usage tab."
           testid="row-usage_logging_enabled">
        <Switch checked={!!settings.usage_logging_enabled}
                onCheckedChange={v => update('usage_logging_enabled', v)}
                data-testid="switch-usage_logging_enabled" />
      </Row>
      <Row title="Monthly token limit" hint="Soft cap. Empty = unlimited." testid="row-monthly_token_limit">
        <Input type="number" min={0} className="w-40"
               value={settings.monthly_token_limit ?? ''}
               onChange={e => update('monthly_token_limit',
                 e.target.value === '' ? null : Number(e.target.value))}
               data-testid="input-monthly_token_limit" />
      </Row>
    </Section>
  );
}

// ───── Knowledge (RAG) ─────────────────────────────────────────────
function SectionKnowledge() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteBody, setPasteBody] = useState('');
  const [pasteSaving, setPasteSaving] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [shopifyBusy, setShopifyBusy] = useState(false);
  const [shopifyLimit, setShopifyLimit] = useState(250);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/ai-copilot/kb/documents`, { headers: authHeaders() });
      setDocs(res.data?.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load knowledge documents');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addPaste = async () => {
    if (!pasteTitle.trim() || !pasteBody.trim()) {
      toast.error('Title and body are required.');
      return;
    }
    setPasteSaving(true);
    try {
      await axios.post(`${API_BASE}/ai-copilot/kb/documents/text`, {
        title: pasteTitle.trim(), body: pasteBody.trim(), kind: 'policy',
      }, { headers: authHeaders() });
      toast.success('Saved to knowledge base.');
      setPasteTitle(''); setPasteBody(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setPasteSaving(false); }
  };

  const scrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScrapeBusy(true);
    try {
      await axios.post(`${API_BASE}/ai-copilot/kb/documents/url`, {
        url: scrapeUrl.trim(),
      }, { headers: authHeaders() });
      toast.success('Page scraped & indexed.');
      setScrapeUrl(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scrape failed');
    } finally { setScrapeBusy(false); }
  };

  const syncShopify = async () => {
    setShopifyBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/ai-copilot/kb/documents/shopify-sync`, {
        limit: Number(shopifyLimit) || 250,
      }, { headers: authHeaders() });
      toast.success(`Synced ${res.data?.synced || 0} products.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Shopify sync failed');
    } finally { setShopifyBusy(false); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm('Delete this document and its chunks?')) return;
    try {
      await axios.delete(`${API_BASE}/ai-copilot/kb/documents/${id}`, { headers: authHeaders() });
      toast.success('Deleted.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearchBusy(true);
    try {
      const res = await axios.get(
        `${API_BASE}/ai-copilot/kb/search?q=${encodeURIComponent(searchQ.trim())}&top_k=5`,
        { headers: authHeaders() }
      );
      setSearchHits(res.data?.chunks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally { setSearchBusy(false); }
  };

  return (
    <Section title="Knowledge base">
      <div className="text-sm text-wt-text-muted mb-3">
        Teach the AI your business facts. Retrieved chunks are injected into every AI reply so customers get grounded answers — not hallucinations. Supports paste, URL scrape, and Shopify catalogue sync.
      </div>

      <div className="text-xs px-3 py-2 rounded-lg bg-wt-gold/10 border border-wt-gold/30 text-wt-text-primary mb-3" data-testid="kb-policies-notice">
        <b>Already using the Policies tab?</b> Good — your existing Delivery / Installation / Return / Payment / Discount / Escalation entries are <b>automatically searched too</b>, no re-pasting needed. Use the Search box below to verify.
      </div>

      <div className="border border-wt-border rounded-lg p-3 mb-3 bg-wt-paper" data-testid="kb-paste-card">
        <div className="flex items-center gap-2 mb-2 text-wt-text-primary text-sm font-medium">
          <FileText className="h-4 w-4" /> Paste a policy or FAQ
        </div>
        <Input placeholder="Title (e.g. Return policy)"
               value={pasteTitle} onChange={e => setPasteTitle(e.target.value)}
               className="mb-2" data-testid="kb-paste-title" />
        <Textarea placeholder="Body — paste plain text. The AI will quote from this."
                  rows={5} value={pasteBody} onChange={e => setPasteBody(e.target.value)}
                  data-testid="kb-paste-body" />
        <div className="mt-2 flex justify-end">
          <Button size="sm" onClick={addPaste} disabled={pasteSaving} data-testid="kb-paste-save">
            {pasteSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
            Add to knowledge base
          </Button>
        </div>
      </div>

      <div className="border border-wt-border rounded-lg p-3 mb-3 bg-wt-paper" data-testid="kb-url-card">
        <div className="flex items-center gap-2 mb-2 text-wt-text-primary text-sm font-medium">
          <Link2 className="h-4 w-4" /> Scrape a help-center / FAQ page
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="https://wooden-twist.com/pages/faq"
                 value={scrapeUrl} onChange={e => setScrapeUrl(e.target.value)}
                 className="flex-1" data-testid="kb-url-input" />
          <Button size="sm" onClick={scrape} disabled={scrapeBusy || !scrapeUrl.trim()}
                  data-testid="kb-url-scrape">
            {scrapeBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
            Scrape & index
          </Button>
        </div>
      </div>

      <div className="border border-wt-border rounded-lg p-3 mb-3 bg-wt-paper" data-testid="kb-shopify-card">
        <div className="flex items-center gap-2 mb-2 text-wt-text-primary text-sm font-medium">
          <ShoppingBag className="h-4 w-4" /> Sync product catalogue (from your existing inventory)
        </div>
        <div className="text-xs text-wt-text-muted mb-2">
          Pulls every product from your Opswarex inventory (already synced from Shopify/Amazon/Flipkart) — titles, descriptions, prices, SKUs, stock per warehouse. Re-run any time to refresh. No Shopify roundtrip needed.
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-wt-text-muted">Max products</Label>
          <Input type="number" min={1} max={1000} value={shopifyLimit}
                 onChange={e => setShopifyLimit(e.target.value)}
                 className="w-24" data-testid="kb-shopify-limit" />
          <Button size="sm" onClick={syncShopify} disabled={shopifyBusy}
                  data-testid="kb-shopify-sync">
            {shopifyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Sync now
          </Button>
        </div>
      </div>

      <div className="border border-wt-border rounded-lg p-3 mb-3 bg-wt-paper" data-testid="kb-search-card">
        <div className="flex items-center gap-2 mb-2 text-wt-text-primary text-sm font-medium">
          <Search className="h-4 w-4" /> Try a query — see what the AI would retrieve
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder='e.g. "what is your return policy?"'
                 value={searchQ} onChange={e => setSearchQ(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && runSearch()}
                 className="flex-1" data-testid="kb-search-input" />
          <Button size="sm" onClick={runSearch} disabled={searchBusy || !searchQ.trim()}
                  data-testid="kb-search-run">
            {searchBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
            Search
          </Button>
        </div>
        {searchHits.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {searchHits.map((c, i) => (
              <div key={i} className="text-xs p-2 rounded bg-wt-bg border border-wt-border" data-testid={`kb-search-hit-${i}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium text-wt-text-primary">{c.title}</span>
                  <Badge variant="outline" className="text-[10px]">score {c.score}</Badge>
                </div>
                <div className="text-wt-text-muted">{(c.text || '').slice(0, 220)}…</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-wt-border rounded-lg p-3 bg-wt-paper" data-testid="kb-doc-list">
        <div className="flex items-center justify-between mb-2">
          <div className="text-wt-text-primary text-sm font-medium">
            Documents <span className="text-wt-text-muted">({docs.length})</span>
          </div>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
        {docs.length === 0 && !loading && (
          <div className="text-xs text-wt-text-muted py-2">
            No documents yet. Add one above — the AI will start using it immediately.
          </div>
        )}
        {docs.map(d => (
          <div key={d.id} className="flex items-center gap-2 py-1.5 border-t border-wt-border first:border-t-0 text-sm"
               data-testid={`kb-doc-${d.id}`}>
            <Badge variant="outline" className="text-[10px] capitalize">{d.kind}</Badge>
            <div className="flex-1 min-w-0">
              <div className="truncate text-wt-text-primary">{d.title}</div>
              <div className="text-[11px] text-wt-text-muted truncate">
                {d.source_url ? `${d.source_url} · ` : ''}{d.chunk_count || 0} chunks
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeDoc(d.id)}
                    className="text-rose-600 hover:text-rose-700"
                    data-testid={`kb-doc-delete-${d.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
}


// ───── Usage roll-up ───────────────────────────────────────────────────
function SectionUsage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/ai-copilot/usage?days=${days}`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.feature_totals || {};
  const totalCalls = useMemo(() =>
    Object.values(totals).reduce((a, b) => a + (b || 0), 0), [totals]);

  return (
    <Section title={`Usage — last ${days} days`}>
      <div className="flex items-center gap-2 mb-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32" data-testid="select-usage-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 day</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-usage">
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Refresh
        </Button>
        <span className="ml-auto text-sm text-wt-text-muted" data-testid="usage-total-calls">
          Total calls: <b className="text-wt-text-primary">{totalCalls}</b>
        </span>
      </div>

      {/* Feature totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
        {Object.entries(totals).map(([feature, count]) => (
          <div key={feature} className="rounded border border-wt-border p-3 bg-white/40"
               data-testid={`feature-total-${feature}`}>
            <div className="text-[10px] uppercase tracking-wide text-wt-text-muted">{feature.replaceAll('_', ' ')}</div>
            <div className="text-lg font-semibold text-wt-text-primary">{count}</div>
          </div>
        ))}
        {Object.keys(totals).length === 0 && (
          <div className="col-span-full text-sm text-wt-text-muted">No usage in the selected window yet.</div>
        )}
      </div>

      {/* Daily breakdown */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="usage-daily-table">
          <thead>
            <tr className="text-left text-xs text-wt-text-muted border-b border-wt-border">
              <th className="py-1.5 pr-3">Day</th>
              <th className="py-1.5 pr-3">Calls</th>
              <th className="py-1.5 pr-3">Tokens</th>
              <th className="py-1.5 pr-3">Features</th>
            </tr>
          </thead>
          <tbody>
            {(data?.by_day || []).map(row => (
              <tr key={row.day} className="border-b border-wt-border/60">
                <td className="py-1.5 pr-3 font-medium">{row.day}</td>
                <td className="py-1.5 pr-3">{row.total_calls}</td>
                <td className="py-1.5 pr-3">{row.total_tokens.toLocaleString()}</td>
                <td className="py-1.5 pr-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(row.features).map(([f, v]) => (
                      <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-wt-paper/80 border border-wt-border">
                        {f}: {v.calls}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.by_day || []).length === 0 && (
              <tr><td colSpan={4} className="py-3 text-center text-wt-text-muted">No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ───── Analytics ───────────────────────────────────────────────────────
function SectionAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/ai-copilot/analytics?days=${days}`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <Section title={`Copilot impact — last ${days} days`}>
      <div className="flex items-center gap-2 mb-3">
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32" data-testid="select-analytics-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 day</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-analytics">
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Refresh
        </Button>
        <span className="ml-auto text-sm text-wt-text-muted" data-testid="analytics-handoff-count">
          Handoffs: <b className="text-wt-text-primary">{data?.handoff_count ?? 0}</b>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel title="Top task reasons">
          {(data?.task_reasons || []).length === 0 ? (
            <Empty />
          ) : (data.task_reasons.map(r => (
            <Bar key={r.reason} label={r.reason} value={r.count} />
          )))}
        </Panel>
        <Panel title="Intent distribution">
          {(data?.intents || []).length === 0 ? (
            <Empty />
          ) : (data.intents.map(i => (
            <Bar key={i.intent} label={i.intent} value={i.count} />
          )))}
        </Panel>
        <Panel title="Lead tiers">
          {(data?.lead_tiers || []).length === 0 ? (
            <Empty />
          ) : (data.lead_tiers.map(t => (
            <Bar key={t.tier} label={t.tier} value={t.count} />
          )))}
        </Panel>
      </div>
    </Section>
  );
}

function Panel({ title, children }) {
  return (
    <div className="border border-wt-border rounded p-3 bg-white/30">
      <div className="text-xs font-semibold uppercase tracking-wide text-wt-text-muted mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Empty() {
  return <div className="text-xs text-wt-text-muted py-2">No data.</div>;
}
function Bar({ label, value }) {
  const max = 100;
  const pct = Math.min(100, (value || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-wt-text-primary">{String(label).replaceAll('_', ' ')}</span>
        <span className="text-wt-text-muted">{value}</span>
      </div>
      <div className="h-1.5 bg-wt-paper rounded overflow-hidden">
        <div className="h-full bg-wt-gold" style={{ width: `${(pct / max) * 100}%` }} />
      </div>
    </div>
  );
}
